import React, { useState } from "react";
import { DataTable, DataTableColumnHeader, Button, InputField } from "@jem-open/jem-ui";
import { ColumnDef } from "@tanstack/react-table";
import { Search, AlertCircle } from "lucide-react";
import { AxiosError } from "axios";
import McmSegment from "../services/model/McmSegment";
import McmSegmentEmployee from "../services/model/McmSegmentEmployee";
import { useMcmSegmentEmployeesApiService } from "../services/api/McmSegmentApiService";
import MultimessagingBreadcrumb from "../elements/MultimessagingBreadcrumb";

type SegmentEmployeeListProps = {
  segment: McmSegment;
  onGoBack: () => void;
};

const employeeColumns: ColumnDef<McmSegmentEmployee>[] = [
  {
    accessorKey: "last_name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Surname" />
    ),
  },
  {
    accessorKey: "first_name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="First name" />
    ),
  },
  {
    accessorKey: "employee_no",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Employee No." />
    ),
    cell: ({ row }) => (
      <span className="text-sm">{row.original.employee_no || "\u2014"}</span>
    ),
  },
];

const SegmentEmployeeList: React.FC<SegmentEmployeeListProps> = ({ segment, onGoBack }) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, error } = useMcmSegmentEmployeesApiService({
    segmentId: segment.id,
    search: search || undefined,
    page,
  });

  const isInactiveAffiliation =
    isError && (error as AxiosError)?.response?.status === 403;
  const employees = data?.results || [];
  const totalPages = data?.pages || 1;

  return (
    <div>
      <MultimessagingBreadcrumb
        items={[
          { label: "Manage Segments", onClick: onGoBack },
          { label: `Employees (${data?.count ?? "..."})` },
        ]}
      />

      <h2 className="text-xl font-heading font-semibold text-greyscale-text-title mb-sm">
        {segment.name} – Employees
      </h2>

      {isInactiveAffiliation ? (
        <div className="flex items-center gap-xs px-md py-sm bg-red-50 rounded-md border border-red-200 mb-md">
          <AlertCircle size={18} className="text-red-500" />
          <span className="text-sm font-semibold text-red-600">
            This segment&apos;s target company is no longer affiliated. Employee data is unavailable.
          </span>
        </div>
      ) : (
        <>
          {/* Search */}
          <div className="relative mb-md max-w-[500px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-greyscale-text-caption pointer-events-none z-10" />
            <InputField
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search employee"
              className="w-full [&_input]:pl-9"
            />
          </div>

          {/* Employee table */}
          {isLoading ? (
            <p className="text-sm text-greyscale-text-caption py-md">Loading employees...</p>
          ) : (
            <DataTable
              columns={employeeColumns}
              data={employees}
              showPagination={false}
              showToolbar={false}
            />
          )}
        </>
      )}

      {!isInactiveAffiliation && totalPages > 1 && (
        <div className="flex items-center justify-center gap-xs mt-md">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-greyscale-text-caption">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-greyscale-border mt-lg" />
      <div className="flex items-center justify-between py-lg">
        <Button variant="outline" onClick={onGoBack}>
          Go Back
        </Button>
      </div>
    </div>
  );
};

export default SegmentEmployeeList;
